import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Self-playing figures, in the style of an animated explainer: each figure is
 * a loop of named phases, and each phase is a few beats long. The figure plays
 * while it is on screen, pauses when it scrolls away, and loops. The phase bar
 * under it doubles as the controls: click a phase to jump there, or use
 * previous, pause and next.
 *
 * Server render and first client render show the last beat of the last phase,
 * so the prerendered HTML is a complete picture. Playback starts from the top
 * the first time the figure scrolls into view. With prefers-reduced-motion the
 * figure never plays on its own; the controls still step through it.
 */

export interface Phase {
  /** Short label for the phase bar, shown in caps. */
  label: string;
  /** Optional group name. Consecutive phases with the same act share a header. */
  act?: string;
  /** Beats in this phase. The last beat is held twice as long. Default 3. */
  beats?: number;
  /** Shown in the bar but never visited, so a shortened run keeps the same bar. */
  skip?: boolean;
}

export interface Seq {
  phase: number;
  beat: number;
  playing: boolean;
  /** True once the reader has the final frame of the current phase on screen. */
  settled: boolean;
  go: (i: number) => void;
  prev: () => void;
  next: () => void;
  toggle: () => void;
  ref: (el: HTMLElement | null) => void;
}

export const beatsOf = (p: Phase) => p.beats ?? 3;

export function useSequence(phases: Phase[], opts: { beatMs?: number; onLoop?: () => void } = {}): Seq {
  const { beatMs = 850, onLoop } = opts;
  const last = phases.length - 1;
  // The next or previous phase that is not skipped, wrapping at the ends.
  const stepFrom = (i: number, dir: 1 | -1) => {
    let j = i;
    do {
      j = (j + dir + phases.length) % phases.length;
    } while (phases[j].skip && j !== i);
    return j;
  };
  const [pos, setPos] = useState({ phase: last, beat: beatsOf(phases[last]) - 1 });
  const [playing, setPlaying] = useState(false);
  const [visible, setVisible] = useState(false);
  const started = useRef(false);
  const node = useRef<HTMLElement | null>(null);
  const io = useRef<IntersectionObserver | null>(null);
  const loopRef = useRef(onLoop);
  loopRef.current = onLoop;

  const ref = useCallback((el: HTMLElement | null) => {
    io.current?.disconnect();
    node.current = el;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    io.current = new IntersectionObserver(
      ([e]) => {
        setVisible(e.isIntersecting);
        if (e.isIntersecting && !started.current) {
          started.current = true;
          if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setPos({ phase: 0, beat: 0 });
            setPlaying(true);
          }
        }
      },
      { threshold: 0.35 },
    );
    io.current.observe(el);
  }, []);

  useEffect(() => () => io.current?.disconnect(), []);

  useEffect(() => {
    if (!playing || !visible) return undefined;
    const beats = beatsOf(phases[pos.phase]);
    const holding = pos.beat === beats - 1;
    const t = window.setTimeout(
      () => {
        if (!holding) {
          setPos({ phase: pos.phase, beat: pos.beat + 1 });
          return;
        }
        const nextPhase = stepFrom(pos.phase, 1);
        if (nextPhase <= pos.phase) loopRef.current?.();
        setPos({ phase: nextPhase, beat: 0 });
      },
      holding ? beatMs * 2.2 : beatMs,
    );
    return () => window.clearTimeout(t);
    // stepFrom only reads `phases`, which is already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, visible, pos, phases, last, beatMs]);

  const go = useCallback(
    (i: number) => {
      const p = Math.max(0, Math.min(last, i));
      if (phases[p].skip) return;
      // Paused: jump to the finished frame so there is something to read.
      setPos({ phase: p, beat: playing ? 0 : beatsOf(phases[p]) - 1 });
    },
    [last, phases, playing],
  );

  return {
    phase: pos.phase,
    beat: pos.beat,
    playing,
    settled: pos.beat === beatsOf(phases[pos.phase]) - 1,
    go,
    prev: () => go(stepFrom(pos.phase, -1)),
    next: () => go(stepFrom(pos.phase, 1)),
    toggle: () => {
      started.current = true;
      setPlaying((p) => !p);
    },
    ref,
  };
}

/** The phase strip under a figure: acts, phases, progress, and transport. */
export function PhaseBar({ phases, seq, beatMs = 850 }: { phases: Phase[]; seq: Seq; beatMs?: number }) {
  const groups: { act?: string; items: { p: Phase; i: number }[] }[] = [];
  phases.forEach((p, i) => {
    const g = groups[groups.length - 1];
    if (g && g.act === p.act) g.items.push({ p, i });
    else groups.push({ act: p.act, items: [{ p, i }] });
  });
  const beats = beatsOf(phases[seq.phase]);
  return (
    <div className="bq-bar">
      <div className="bq-phases" role="group" aria-label="Phases">
        {groups.map((g, gi) => (
          <div className="bq-act" key={gi}>
            {g.act && <span className="bq-act-name">{g.act}</span>}
            <div className="bq-chips">
              {g.items.map(({ p, i }) => {
                const cur = i === seq.phase;
                return (
                  <button
                    key={i}
                    type="button"
                    className={`bq-chip${p.skip ? ' skip' : i < seq.phase ? ' done' : ''}`}
                    aria-current={cur ? 'step' : undefined}
                    disabled={p.skip}
                    onClick={() => seq.go(i)}
                  >
                    <span>{p.label}</span>
                    <i
                      aria-hidden="true"
                      style={{
                        transform: `scaleX(${cur ? (seq.beat + 1) / beats : i < seq.phase ? 1 : 0})`,
                        transitionDuration: cur && seq.playing ? `${beatMs}ms` : '0ms',
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="bq-ctl">
        <button type="button" onClick={seq.prev} aria-label="Previous phase">
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M10 3 5 8l5 5" />
          </svg>
        </button>
        <button type="button" onClick={seq.toggle} aria-label={seq.playing ? 'Pause' : 'Play'}>
          {seq.playing ? (
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M5.5 3v10M10.5 3v10" />
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path className="fill" d="M5 3v10l8-5z" />
            </svg>
          )}
        </button>
        <button type="button" onClick={seq.next} aria-label="Next phase">
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="m6 3 5 5-5 5" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/** Every (phase, beat) a sequence passes through, in order. */
export function positions(phases: Phase[]): [number, number][] {
  return phases.flatMap((p, i) =>
    p.skip ? [] : Array.from({ length: beatsOf(p) }, (_, b): [number, number] => [i, b]),
  );
}

/**
 * Holds its place at the size of the largest of `all`: every option is laid out
 * invisibly in one grid cell under the live content, so swapping text in and
 * out never moves anything around it.
 */
export function Steady({
  all,
  children,
  block = false,
  className = '',
}: {
  all: ReactNode[];
  children: ReactNode;
  block?: boolean;
  className?: string;
}) {
  const Tag = block ? 'div' : 'span';
  return (
    <Tag className={`bq-steady${block ? ' blk' : ''} ${className}`.trim()}>
      {all.map((n, k) => (
        <Tag key={k} className="bq-sizer" aria-hidden="true">
          {n}
        </Tag>
      ))}
      <Tag>{children}</Tag>
    </Tag>
  );
}

/**
 * A self-playing figure: title, stage, narration line, phase bar.
 *
 * `narrate` gives the line for any (phase, beat). Every line the figure can
 * show is rendered invisibly in the same grid cell as the live one, so the
 * narration box is always as tall as its longest line at the current width
 * and the figure never changes height while it plays. `sizers` replaces that
 * set when the figure can show lines from outside its own phases.
 */
export function Animated({
  title,
  seq,
  phases,
  narrate,
  sizers,
  children,
  beatMs,
}: {
  title: ReactNode;
  seq: Seq;
  phases: Phase[];
  narrate: (phase: number, beat: number) => ReactNode;
  sizers?: ReactNode[];
  children: ReactNode;
  beatMs?: number;
}) {
  const all = sizers ?? positions(phases).map(([i, b]) => narrate(i, b));
  return (
    <div className="bw bq" ref={seq.ref}>
      <div className="bw-top">
        <h3>{title}</h3>
      </div>
      <div className="bq-stage">{children}</div>
      <div className="bq-say-box">
        {all.map((n, k) => (
          <p key={k} className="bq-say bq-sizer" aria-hidden="true">
            {n}
          </p>
        ))}
        <p className="bq-say" aria-live="polite">
          {narrate(seq.phase, seq.beat)}
        </p>
      </div>
      <PhaseBar phases={phases} seq={seq} beatMs={beatMs} />
    </div>
  );
}

// ─── SVG pieces ─────────────────────────────────────────────────────────────

export type Tone = 'ink' | 'ok' | 'no' | 'hold' | 'clay' | 'dim';

/** A labelled box. `tone` colours the frame; `on` lifts it. */
export function Box({
  x,
  y,
  w,
  h,
  label,
  sub,
  tone = 'ink',
  on = false,
  kicker,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  sub?: string;
  tone?: Tone;
  on?: boolean;
  kicker?: string;
}) {
  return (
    <g className={`bq-box t-${tone}${on ? ' on' : ''}`} transform={`translate(${x} ${y})`}>
      <rect width={w} height={h} rx={4} />
      <rect className="key" width={4} height={h} rx={2} />
      {kicker && (
        <text className="bq-kick" x={14} y={18}>
          {kicker}
        </text>
      )}
      <text className="bq-lab" x={14} y={kicker ? 38 : h / 2 - (sub ? 7 : 0)} dominantBaseline="central">
        {label}
      </text>
      {sub && (
        <text className="bq-sub" x={14} y={kicker ? 56 : h / 2 + 12} dominantBaseline="central">
          {sub}
        </text>
      )}
    </g>
  );
}

/** A straight wire between two points. */
export function Wire({
  x1,
  y1,
  x2,
  y2,
  tone = 'dim',
  dashed = false,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  tone?: Tone;
  dashed?: boolean;
}) {
  return <line className={`bq-wire t-${tone}${dashed ? ' dash' : ''}`} x1={x1} y1={y1} x2={x2} y2={y2} />;
}

/**
 * A request in flight. It glides to (x, y) whenever they change, so moving a
 * packet across the diagram is a matter of rendering it at the next point.
 */
export function Packet({
  x,
  y,
  label,
  tone = 'clay',
  show = true,
}: {
  x: number;
  y: number;
  label: string;
  tone?: Tone;
  show?: boolean;
}) {
  const w = Math.max(44, label.length * 7.4 + 18);
  return (
    <g className={`bq-pk t-${tone}${show ? '' : ' gone'}`} style={{ transform: `translate(${x}px, ${y}px)` }}>
      <rect x={-w / 2} y={-11} width={w} height={22} rx={11} />
      <text x={0} y={0} textAnchor="middle" dominantBaseline="central">
        {label}
      </text>
    </g>
  );
}

/** A small status tag in the diagram (200, 403, blocked, ...). */
export function Tag({
  x,
  y,
  label,
  tone,
  show = true,
}: {
  x: number;
  y: number;
  label: string;
  tone: Tone;
  show?: boolean;
}) {
  const w = label.length * 7 + 14;
  return (
    <g className={`bq-tag t-${tone}${show ? '' : ' gone'}`} transform={`translate(${x} ${y})`}>
      <rect x={-w / 2} y={-9} width={w} height={18} rx={3} />
      <text x={0} y={0} textAnchor="middle" dominantBaseline="central">
        {label}
      </text>
    </g>
  );
}
